/**
 * Application Entry Point -- Xyden Leadership Assessment Platform
 * Step 9F Green Phase
 *
 * Mounts the React app with BrowserRouter and AppProviders.
 * MSW is always initialized in development to mock the backend API
 * (backend is not yet available — Step 11 generates it).
 */

import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { AppProviders } from "./providers/AppProviders";
import { App } from "./App";
import { registerGlobalErrorHandlers } from "./lib/telemetry";
import "./index.css";

// First-party telemetry (NFR-F-OBS-001): catch uncaught window errors and
// unhandled promise rejections globally — the layer the React error boundaries
// can't see — and ship them to the in-house log sink. Registered once, before
// render, so an error during initial mount is captured too. Idempotent.
registerGlobalErrorHandlers();

async function enableMocking(): Promise<void> {
  if (
    import.meta.env.VITE_ENABLE_MOCKS !== "true" &&
    import.meta.env.VITE_E2E_MODE !== "true"
  ) {
    return;
  }

  const { worker } = await import("./__mocks__/browser");
  await worker.start({
    onUnhandledRequest: "warn",
    serviceWorker: { url: "/mockServiceWorker.js" },
  });
}

enableMocking().then(() => {
  ReactDOM.createRoot(document.getElementById("root")!).render(
    <React.StrictMode>
      <BrowserRouter>
        <AppProviders>
          <App />
        </AppProviders>
      </BrowserRouter>
    </React.StrictMode>
  );
});
